import { createServerFn } from "@tanstack/react-start";
import { asc } from "drizzle-orm";
import z from "zod";
import { db } from "~/db";
import { eventType } from "~/db/schema";

export type EventType = {
  id: number;
  name: string;
};

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
let cachedEventTypes: { data: EventType[]; timestamp: number } | null = null;

const GetEventTypesSchema = z.object({
  ids: z.array(z.number()).optional(),
  names: z.array(z.string()).optional(),
});

export const getEventTypes = createServerFn({ method: "GET" })
  .inputValidator(GetEventTypesSchema.optional())
  .handler(async (ctx) => {
    const ids = ctx.data?.ids;
    const names = ctx.data?.names;

    // Always use cache if available and not expired, then filter client-side if needed
    const now = Date.now();
    if (cachedEventTypes && now - cachedEventTypes.timestamp < CACHE_TTL_MS) {
      console.log("Serving event types from cache.");
      const types = cachedEventTypes.data;

      // If names are provided, convert them to IDs first
      let finalIds = ids;
      if (names && names.length > 0) {
        const nameMap = new Map(types.map((type) => [type.name, type.id]));
        finalIds = names
          .map((name) => nameMap.get(name))
          .filter((id): id is number => id !== undefined);
      }

      // If IDs are provided (either directly or converted from names), filter from cache and preserve order
      if (finalIds && finalIds.length > 0) {
        const typeMap = new Map(types.map((type) => [type.id, type]));
        return finalIds
          .map((id) => typeMap.get(id))
          .filter((type): type is EventType => type !== undefined);
      }
      return types;
    }
    if (cachedEventTypes && now - cachedEventTypes.timestamp >= CACHE_TTL_MS) {
      cachedEventTypes = null;
    }

    // If the cache is empty, query the database.
    console.log("Cache miss. Fetching event types from database...");
    const allTypes = await db
      .select({
        id: eventType.id,
        name: eventType.name,
      })
      .from(eventType)
      .groupBy(eventType.id, eventType.name)
      .orderBy(asc(eventType.name));

    // Populate the cache with the data from the database.
    cachedEventTypes = { data: allTypes, timestamp: Date.now() };

    // If names are provided, convert them to IDs first
    let finalIds = ids;
    if (names && names.length > 0) {
      const nameMap = new Map(
        allTypes.map((type) => [type.name, type.id])
      );
      finalIds = names
        .map((name) => nameMap.get(name))
        .filter((id): id is number => id !== undefined);
    }

    // If IDs are provided (either directly or converted from names), filter the results and preserve order
    if (finalIds && finalIds.length > 0) {
      const typeMap = new Map(
        allTypes.map((type) => [type.id, type])
      );
      return finalIds
        .map((id) => typeMap.get(id))
        .filter((type): type is EventType => type !== undefined);
    }

    return allTypes;
  });
